<template>
  <div v-if="hasActions" class="relative">
    <button
      class="p-2 hover:bg-gray-100 rounded-full transition-colors"
      aria-label="Payment actions"
      @click.stop="toggleMenu"
    >
      <i class="pi pi-ellipsis-v text-gray-600" />
    </button>

    <Menu ref="menuRef" :model="menuItems" :popup="true" />

    <ReschedulePaymentDialog
      v-if="showRescheduleDialog"
      v-model:visible="showRescheduleDialog"
      :payment-id="payment.id"
      :current-date="payment.scheduled_at"
      @success="handleActionSuccess"
    />

    <ChargeNowDialog
      v-if="showChargeNowDialog"
      v-model:visible="showChargeNowDialog"
      :payment="payment"
      @success="handleActionSuccess"
    />

    <ChangePaymentMethodDrawer
      v-if="showChangeMethodDrawer && associatedContactId"
      v-model:visible="showChangeMethodDrawer"
      :payment-id="payment.id"
      :associated-contact-id="associatedContactId"
      @success="handleActionSuccess"
    />

    <AmendPaymentDrawer
      v-if="showAmendDrawer"
      v-model:visible="showAmendDrawer"
      :payment-id="payment.id"
      :current-amount="payment.amount ?? 0"
      @success="handleActionSuccess"
    />
  </div>
</template>

<script setup lang="ts">
import { ref, computed } from "vue";
import Menu from "primevue/menu";
import { useToast } from "primevue/usetoast";
import ReschedulePaymentDialog from "./ReschedulePaymentDialog.vue";
import ChargeNowDialog from "./ChargeNowDialog.vue";
import ChangePaymentMethodDrawer from "./ChangePaymentMethodDrawer.vue";
import AmendPaymentDrawer from "./AmendPaymentDrawer.vue";
import {
  PaymentsLogStatusEnum,
  type PaymentsLog,
  type PaymentsLogDetail,
} from "@/api/model";
import { adminV1OrganizationsPaymentsScheduledPaymentsResendFailedNotificationCreate } from "@/api/admin-payments-payment-actions/admin-payments-payment-actions";
import { useCurrentOrganization } from "@/composables/useCurrentOrganization";
import { TERMINAL_PAYMENT_SOURCE_TYPE } from "@/modules/payments/constants";

interface Props {
  payment: PaymentsLog | PaymentsLogDetail;
  subscriberMembershipId?: string;
  associatedContactId?: number | null;
}

const props = defineProps<Props>();

const emit = defineEmits<{
  "action-success": [];
  refund: [payment: PaymentsLog | PaymentsLogDetail];
}>();

const toast = useToast();
const { currentOrganizationId } = useCurrentOrganization();

const menuRef = ref();
const showRescheduleDialog = ref(false);
const showChargeNowDialog = ref(false);
const isResendingNotification = ref(false);
const showChangeMethodDrawer = ref(false);
const showAmendDrawer = ref(false);

// Cash, waived and free (MAI-4959) rows carry no gateway transaction, so
// refund / charge / change-method never apply.
const isNonRevenuePayment = computed(() => {
  const method = props.payment.payment_method_type?.toLowerCase() ?? "";
  return method === "cash" || method === "waive" || method === "free";
});

const associatedContactId = computed(() => {
  if (props.associatedContactId) {
    return props.associatedContactId;
  }
  if ("associated_contact_id" in props.payment) {
    return (props.payment as PaymentsLogDetail).associated_contact_id ?? null;
  }
  return null;
});

const canReschedule = computed(() => {
  return props.payment.status === PaymentsLogStatusEnum.SCHEDULED;
});

const canChargeNow = computed(() => {
  const chargeableStatuses: PaymentsLogStatusEnum[] = [
    PaymentsLogStatusEnum.SCHEDULED,
    PaymentsLogStatusEnum.FAILED,
    PaymentsLogStatusEnum.PAST_DUE,
    PaymentsLogStatusEnum.RETRY,
  ];

  if (!chargeableStatuses.includes(props.payment.status)) {
    return false;
  }

  if (isNonRevenuePayment.value) return false;
  if (!props.payment.payment_method) return false;

  return true;
});

const canAmend = computed(() => {
  // Amend allowed on unpaid statuses: SCHEDULED, FAILED, PAST_DUE.
  const amendableStatuses: PaymentsLogStatusEnum[] = [
    PaymentsLogStatusEnum.SCHEDULED,
    PaymentsLogStatusEnum.FAILED,
    PaymentsLogStatusEnum.PAST_DUE,
  ];
  if (!amendableStatuses.includes(props.payment.status)) return false;
  if (isNonRevenuePayment.value) return false;
  return true;
});

const canChangeMethod = computed(() => {
  const changeableStatuses: PaymentsLogStatusEnum[] = [
    PaymentsLogStatusEnum.SCHEDULED,
    PaymentsLogStatusEnum.FAILED,
    PaymentsLogStatusEnum.PAST_DUE,
    PaymentsLogStatusEnum.RETRY,
  ];

  if (!changeableStatuses.includes(props.payment.status)) {
    return false;
  }

  if (isNonRevenuePayment.value) return false;
  if (!associatedContactId.value) return false;

  return true;
});

const canRefund = computed(() => {
  if (
    props.payment.source_type !== "subscriber_membership" &&
    props.payment.source_type !== TERMINAL_PAYMENT_SOURCE_TYPE
  ) {
    return false;
  }

  return (
    props.payment.status === PaymentsLogStatusEnum.PAID &&
    !props.payment.refund_transaction_created_at &&
    !isNonRevenuePayment.value
  );
});

const canResendFailedNotification = computed(() => {
  if (props.payment.status !== PaymentsLogStatusEnum.FAILED) {
    return false;
  }
  const supportedSourceTypes = ["subscriber_membership", "invoice"];
  return supportedSourceTypes.includes(props.payment.source_type ?? "");
});

const hasActions = computed(() => {
  return (
    canReschedule.value ||
    canChargeNow.value ||
    canRefund.value ||
    canChangeMethod.value ||
    canAmend.value ||
    canResendFailedNotification.value
  );
});

const menuItems = computed(() => {
  const items = [];

  if (canReschedule.value) {
    items.push({
      label: "Change payment date",
      icon: "pi pi-calendar",
      command: () => {
        showRescheduleDialog.value = true;
      },
    });
  }

  if (canChangeMethod.value) {
    items.push({
      label: "Change method",
      icon: "pi pi-arrow-right-arrow-left",
      command: () => {
        showChangeMethodDrawer.value = true;
      },
    });
  }

  if (canAmend.value) {
    items.push({
      label: "Change amount or waive",
      icon: "pi pi-pencil",
      command: () => {
        showAmendDrawer.value = true;
      },
    });
  }

  if (canChargeNow.value) {
    items.push({
      label: "Charge now",
      icon: "pi pi-credit-card",
      command: () => {
        showChargeNowDialog.value = true;
      },
    });
  }

  if (canRefund.value) {
    items.push({
      label: "Refund",
      icon: "pi pi-replay",
      command: () => {
        emit("refund", props.payment);
      },
    });
  }

  if (canResendFailedNotification.value) {
    items.push({
      label: "Resend payment reminder",
      icon: "pi pi-envelope",
      disabled: isResendingNotification.value,
      command: handleResendFailedNotification,
    });
  }

  return items;
});

const toggleMenu = (event: Event) => {
  menuRef.value?.toggle(event);
};

const handleResendFailedNotification = async () => {
  if (!currentOrganizationId.value) {
    toast.add({
      severity: "error",
      summary: "Error",
      detail: "No organization selected",
      life: 5000,
    });
    return;
  }

  isResendingNotification.value = true;
  try {
    await adminV1OrganizationsPaymentsScheduledPaymentsResendFailedNotificationCreate(
      currentOrganizationId.value,
      props.payment.id
    );
    toast.add({
      severity: "success",
      summary: "Reminder sent",
      detail: "Payment reminder email has been sent",
      life: 3000,
    });
  } catch {
    toast.add({
      severity: "error",
      summary: "Error",
      detail: "Failed to send payment reminder",
      life: 5000,
    });
  } finally {
    isResendingNotification.value = false;
  }
};

const handleActionSuccess = () => {
  emit("action-success");
};
</script>
